import { useCallback, useEffect, useMemo } from 'react';
import {
  ResourceReservationsToDayMapsType,
  ResourceViolationItem,
  SchedulerDayColumn,
  ServiceRulesViolations,
} from '@lp/features/Scheduler/types';
import { baseRowClassname } from '@lp/features/Scheduler/constants';
import { getScheduleDayViolations } from '@lp/features/Scheduler/utils';
import { DayColumnHeader, UserProfileCell, UserProfileHeader } from '@lp/features/Scheduler/components';
import { useDayColumns, useSchedulerTableAutoScroll, useSchedulerUserRows } from '@lp/features/Scheduler/hooks';
import { SOrganGroup } from '@lp/features/Scheduler/SchedulerSurgeons/types';
import { getSShiftSetData } from '@lp/features/Scheduler/SchedulerSurgeons/utils';
import { S_SHIFT_TYPE_SELECT_LIST } from '@lp/features/Scheduler/SchedulerSurgeons/constants';
import { getUseSSchedulerFiltersStore } from '@lp/features/Scheduler/SchedulerSurgeons/factories';
import { useSSchedulerHighlightStore } from '@lp/core/store/scheduler';
import {
  Region,
  SResource,
  SResourcesWithShifts,
  SetSShiftData,
  SShiftSelectPayload,
} from '@/api/apis/resources/schedule';
import { useSShiftSiblingMaps } from './hooks';
import { SDayCell, SOnCallTotalsSubTable, SReservationCell, SUserCellSubtitle } from './components';

type SSchedulerTableProps = {
  region: Region;
  organGroup: SOrganGroup;
  isEditable: boolean;
  isReservationsView: boolean;
  canResetShifts: boolean;
  canUnblockShifts?: boolean;
  selectedDate: Date;
  daysInterval: Date[];
  userList: SResource[];
  shiftsData: SResourcesWithShifts;
  reservationsMap?: ResourceReservationsToDayMapsType;
  regionsDictionary: Record<string, Region>;
  onShiftChange: (resourceId: string, day: string, payload?: SetSShiftData) => void;
  onUnblockShift?: (resourceId: string, day: string, scheduleId: string) => void;
  regionViolations?: ServiceRulesViolations;
  resourceViolations?: Record<string, ResourceViolationItem>;
};

export const SSchedulerTable = ({
  region,
  organGroup,
  isEditable,
  isReservationsView,
  canResetShifts,
  canUnblockShifts,
  selectedDate,
  daysInterval,
  userList,
  regionViolations,
  resourceViolations,
  shiftsData,
  reservationsMap,
  regionsDictionary,
  onShiftChange,
  onUnblockShift,
}: SSchedulerTableProps) => {
  const filters = getUseSSchedulerFiltersStore(organGroup)();
  const highlightValue = useSSchedulerHighlightStore.use.value();
  const { scrollContainerRef, rowRefCallback, columnRefCallback, scrollToResourceRow, scrollToDayColumn } =
    useSchedulerTableAutoScroll(selectedDate);
  const dayCols = useDayColumns(daysInterval, selectedDate);
  const siblingMaps = useSShiftSiblingMaps(dayCols, userList, shiftsData, region.id, regionsDictionary);
  const { userRows, sortAsc, toggleSortAsc, daysFilters, setDayFilter } = useSchedulerUserRows({
    dayCols,
    userList,
    shiftsData,
    filters,
  });

  useEffect(() => {
    if (highlightValue?.organGroup === organGroup && highlightValue?.regionId === region.id) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          scrollToResourceRow(highlightValue.resourceId, true);
          scrollToDayColumn(highlightValue.day, true);
        }, 250);
      });
    }
  }, [highlightValue, organGroup, region.id, scrollToDayColumn, scrollToResourceRow]);

  const getShiftChangeHandler = useCallback(
    (resource: SResource, day: string) => (shiftPayload?: SShiftSelectPayload) => {
      if (!shiftPayload) {
        onShiftChange(resource.id, day);
        return;
      }

      const changeData = getSShiftSetData(shiftPayload, resource, region.id);

      onShiftChange(resource.id, day, changeData);
    },
    [onShiftChange, region.id],
  );

  const getUnblockHandler = useCallback(
    (resourceId: string, day: string) => () => {
      const shift = shiftsData[resourceId]?.days[day];
      if (shift && onUnblockShift) {
        onUnblockShift(resourceId, day, shift.scheduleId);
      }
    },
    [shiftsData, onUnblockShift],
  );

  const renderShiftCell = useCallback(
    (column: SchedulerDayColumn, resourceId: string) => {
      const day = column.key;
      const { resource } = shiftsData[resourceId];
      const shift = shiftsData[resourceId].days[day];
      const siblingData = siblingMaps[resourceId].get(day);
      const handler = getShiftChangeHandler(resource, day);
      const highlight =
        highlightValue?.resourceId === resourceId && highlightValue?.day.toISOString() === column.date.toISOString();

      return (
        <SDayCell
          key={column.key}
          column={column}
          shiftData={shift}
          resource={resource}
          siblingData={siblingData}
          isEditable={isEditable}
          canReset={canResetShifts}
          canUnblock={canUnblockShifts}
          highlight={highlight}
          onShiftChange={handler}
          onUnblock={canUnblockShifts ? getUnblockHandler(resourceId, day) : undefined}
        />
      );
    },
    [
      isEditable,
      canResetShifts,
      canUnblockShifts,
      shiftsData,
      siblingMaps,
      highlightValue,
      getShiftChangeHandler,
      getUnblockHandler,
    ],
  );

  const renderReservationCell = useCallback(
    (column: SchedulerDayColumn, resourceId: string) => {
      const day = column.key;
      const shift = shiftsData[resourceId].days[day];
      const reservations = reservationsMap?.[resourceId]?.[day];

      return (
        <SReservationCell
          key={column.key}
          organGroup={organGroup}
          column={column}
          shiftData={shift}
          reservations={reservations}
        />
      );
    },
    [shiftsData, reservationsMap, organGroup],
  );

  const renderDayCell = useMemo(() => {
    return isReservationsView ? renderReservationCell : renderShiftCell;
  }, [isReservationsView, renderReservationCell, renderShiftCell]);

  return (
    <div className="relative">
      <div ref={scrollContainerRef} className="w-full overflow-auto">
        <table aria-label="Schedule table" className="relative border-separate border-spacing-0 pb-4 pr-4">
          <thead>
            <tr className="h-10 text-left [&>th]:px-3 [&>th]:text-s-sb first:[&>th]:rounded-s-lg last:[&>th]:rounded-e-lg">
              <UserProfileHeader sortAsc={sortAsc} toggleSortAsc={toggleSortAsc} />
              {dayCols.map((column) => (
                <DayColumnHeader
                  ref={(el) => columnRefCallback(el, column.date)}
                  key={column.key}
                  column={column}
                  withFilter={filters.statusFilters}
                  filterOptionList={S_SHIFT_TYPE_SELECT_LIST}
                  violations={getScheduleDayViolations(column.key, regionViolations)}
                  selectedKeys={daysFilters[column.key]}
                  onSelectedKeysChange={(shiftTypes) => setDayFilter(column.key, shiftTypes)}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {userRows.map((user) => (
              <tr key={user.id} ref={(el) => rowRefCallback(user.id, el)} className={baseRowClassname}>
                <UserProfileCell key="user" user={user} violation={resourceViolations?.[user.id]}>
                  <SUserCellSubtitle organs={user.organs} />
                </UserProfileCell>
                {dayCols.map((column) => renderDayCell(column, user.id))}
              </tr>
            ))}
            <SOnCallTotalsSubTable regionId={region.id} dayCols={dayCols} userList={userList} shiftsData={shiftsData} />
          </tbody>
        </table>
      </div>
    </div>
  );
};
